import type { ShownLink } from 'tabview/assistantBlocks/compileArrangement';
import type { BlocksViewLink, BlocksViewNode } from '../types';

type Cell = Pick<BlocksViewNode, 'col' | 'row' | 'colSpan'>;

/** Whether a child sits below its parent and within the columns the parent spans. */
const sitsBeneath = (parent: Cell, child: Cell): boolean =>
  child.row > parent.row &&
  child.col >= parent.col &&
  child.col + child.colSpan <= parent.col + parent.colSpan;

/** Each tree child's parent, built once per rebuild so every link is looked up directly. */
export type TreeParentIndex = ReadonlyMap<string, string>;

export const indexTreeLinks = (treeLinks: ShownLink[]): TreeParentIndex =>
  new Map(treeLinks.map(({ parentId, childId }) => [childId, parentId]));

/** The parent end of a link, if it is a parent/child pair the tree arrangement encodes. */
export const toTreeParent = (
  parentByChild: TreeParentIndex,
  source: BlocksViewNode,
  target: BlocksViewNode
): BlocksViewNode | undefined => {
  if (parentByChild.get(target.id) === source.id) return source;
  if (parentByChild.get(source.id) === target.id) return target;
  return undefined;
};

/**
 * Whether the layout itself shows a link, as it now stands: a tree pair whose child sits beneath
 * its parent. Checked on every layout pass, so a drag that breaks the arrangement brings it back.
 */
export const isShownByLayout = ({
  treeParent,
  source,
  target,
}: BlocksViewLink): boolean => {
  if (!treeParent) return false;
  const child = treeParent === source ? target : source;
  return child.parent === treeParent.parent && sitsBeneath(treeParent, child);
};

/** The link tagged with its parent end when it is a parent/child pair the tree encodes. */
export const withTreeParent = (
  parentByChild: TreeParentIndex,
  link: BlocksViewLink
): BlocksViewLink => {
  const treeParent = toTreeParent(parentByChild, link.source, link.target);
  return treeParent ? { ...link, treeParent } : link;
};
